// A server: channels (the server menu switches servers, #140), the live chat, members. One Room (WebSocket) per open server.
import { useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { DmAiNotice } from "../components/DmAiNotice.tsx";
import { Link, Navigate, useMatch, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { canManageChannels, dmHidden, effectiveNotifyLevel, isConversationMuted, messageLinkPath, type MessageLinkRef } from "@bloom/shared";
import { appUsableBy, canTalkTo, BLOOM_ID, BLOOM_NAME, canAddTo, muteActive, decodeChannelRefs, decodeMentions, isThreadStream, markdownToPlain, SPOILER_MASK, threadParentId as parentOf, threadStreamId, type Channel, type ListItem, type BotInfo, type Member, type ParsedCommand } from "@bloom/shared";
import { PrivateCardView, type CardHost } from "../components/AppCard.tsx";
import { AppPage, AppsHome } from "./Apps.tsx";
import { IntegrationSettings } from "../components/IntegrationSettings.tsx";
import { BloomChip, BloomingView, canEditThread, NewAppDialog } from "./Blooming.tsx";
import type { BloomRows } from "../components/MessageList.tsx";
import { api, auth } from "../api.ts";
import { useSession } from "../session.tsx";
import { Room, type ChatMessage, type Snapshot } from "../realtime.ts";
import { useRefusalToast } from "../useRefusalToast.ts";
import { markColdStart, record, setMetricsServer } from "../metrics.ts";
import { liveSince } from "../buildInfo.ts";
import { Avatar } from "../components/ui.tsx";
import { rememberAvatars, rememberBots } from "../people.ts";
import { MessageList, type ChannelRefs, type ListHost } from "../components/MessageList.tsx";
import { ListPanel, NewListDialog, noteListUse, PinIcon } from "../components/Lists.tsx";
import { formatStamp } from "../components/Markdown.tsx";
import { CloseButton } from "../components/icons.tsx";
import { Composer, type ChannelOption, type ComposerHandle, type PersonOption } from "../components/Composer.tsx";
import { InviteModal } from "../components/InviteModal.tsx";
import { DmHeader, dmLabel, shownDms } from "../components/DmList.tsx";
import { useChatLayout } from "../layout.ts";
import { NameColorsContext, nameLook } from "../coloredNames.ts";
import { applyProfile } from "../memberProfile.ts";
import { useSidebarModel } from "../sidebars.tsx";
import type { SidebarProps } from "../components/SidebarProps.ts";
import { CHANNEL_MODES, nickEntries, startLines } from "../components/irc/ircLogic.ts";
import { IrcModes, IrcNickList } from "../components/irc/IrcNickList.tsx";
import { NewDmPicker } from "../components/NewDmPicker.tsx";
import { BotMenu, MemberMenu } from "../components/MemberMenu.tsx";
import { DesktopRowMenu, RowActionGroup, type MenuOrigin } from "../components/ChannelRowMenu.tsx";
import { NotifyBell, NotifyMenu } from "../components/NotifyBell.tsx";
import { HeaderMore } from "../components/HeaderMore.tsx";
import { Sheet } from "../components/Sheet.tsx";
import type { MessageLinkHost } from "../components/MessageLinks.ts";
import { SearchIcon, SearchPanel } from "../components/Search.tsx";
import { isNarrow, useSwipe } from "../mobile.ts";
import { channelUrl, rowActions } from "../rowMenuLogic.ts";
import { QuickSwitcher, type SwitcherItem } from "../components/QuickSwitcher.tsx";
import { focusIfIdle } from "../focusIdle.ts";
import { useMyChats } from "../chats.ts";
import { chatTitle } from "../chatsLogic.ts";
import { ForwardDialog } from "../components/ForwardDialog.tsx";
import type { Forward } from "@bloom/shared";
import { channelLabel, isUnread, sidebarOrder, stepTarget, useTitleBadge, useVisible } from "../readState.ts";
import { useChatShortcuts } from "../shortcuts.ts";
import { AWAY_SETTLE_MS, awayDivider, awayLoadBack, watchAway, type AwayMark } from "../awayWatch.ts";
import { ServerSettings } from "./ServerSettings.tsx";
import { useMutedIds } from "../muteClock.ts";
import { useServerUnread } from "../serverUnread.ts";
import { ServerNotifySettings } from "../components/ServerNotifySettings.tsx";
import "../components/server-settings.css";
import { ServerEmojiProvider } from "../customEmoji.tsx";
import { lastChannelIn, lastChatsOpen, rememberChatsOpen, rememberVisit } from "../lastVisit.ts";
import { useBackCloses } from "../backCloses.ts";
import { describeShare, dropShare, onShareChange, sendShareTo, shareWaiting, waitingShare } from "../share.ts";

// Desktop: the member list hidden or shown (the people button in the channel header), remembered on this device for
// every server, like Discord. Storage can be missing or throw (private windows, blocked site data): then it's shown.
const MEMBERS_HIDDEN_KEY = "bloom:members-hidden";
function loadMembersHidden(): boolean {
  try {
    return localStorage.getItem(MEMBERS_HIDDEN_KEY) === "1";
  } catch {
    return false;
  }
}
function saveMembersHidden(hidden: boolean) {
  try {
    if (hidden) localStorage.setItem(MEMBERS_HIDDEN_KEY, "1");
    else localStorage.removeItem(MEMBERS_HIDDEN_KEY);
  } catch {
    // Not remembered; it still applies until the page reloads.
  }
}

const EMPTY: Snapshot = { status: "connecting", me: null, categories: [], channels: [], byChannel: {}, online: new Set(), typing: {}, read: {}, mentionCounts: {}, notify: {}, mutes: {}, dmClosed: {}, serverNotify: {}, synced: false, apps: [], privateCards: [], emojis: [], stickers: [], lists: [], privateItems: {}, blooming: [], bloomWho: "everyone", integrations: [], bots: [], notice: null };
const noop = () => () => {};

function TypingLine({ text }: { text: React.ReactNode }) {
  return (
    <div className="typing" aria-live="polite">
      {text && (
        <>
          <span className="typing-dots" aria-hidden="true"><span /><span /><span /></span>
          {text}
        </>
      )}
    </div>
  );
}

export function Server() {
  const { serverId = "", channelId: channelParam, messageId: messageParam, section: settingsSection, focus: settingsFocus, slug: appSlug, tab: appTab, threadId: bloomParam } = useParams();
  /** /s/:id/settings…: the settings page is open over this server (the connection stays up underneath). */
  const inSettings = !!useMatch("/s/:serverId/settings/*");
  /** /s/:id/apps[/:slug[/:tab]]: the app home, or an app's page, in place of the chat. */
  const inApps = !!useMatch("/s/:serverId/apps/*");
  /** /s/:id/bloom/:threadId: a blooming thread (conversation and live preview) in place of the chat. */
  const inBloom = !!useMatch("/s/:serverId/bloom/:threadId");
  const [search] = useSearchParams();
  /** The conversation last open (apps post their cards there, and Back returns to it). */
  const lastChannel = useRef<string | undefined>(undefined);
  // "New app" or "New bot": apps and bots are made from their own entry points (docs/bots.md).
  const [newApp, setNewApp] = useState<"app" | "bot" | null>(null);
  const [commandNote, setCommandNote] = useState<string | null>(null);
  const { me, refresh, signOut } = useSession();
  const navigate = useNavigate();
  const [roomState, setRoom] = useState<Room | null>(null);
  const room = roomState?.serverId === serverId ? roomState : null;
  const [members, setMembers] = useState<Member[]>([]);
  const [inviting, setInviting] = useState(false);
  const [notifySettings, setNotifySettings] = useState(false);
  /** A conversation's menu in the channel list (long press on phones, right-click, the Menu key): in a sheet, or a popover on desktop. */
  const [notifyFor, setNotifyFor] = useState<{ id: string; origin: MenuOrigin } | null>(null);
  /** "Link copied", for a moment. */
  const [linkToast, setLinkToast] = useState<string | null>(null);
  const layout = useChatLayout();
  const [navOpen, setNavOpen] = useState(() => layout === "chats" && isNarrow() && lastChatsOpen());
  // Switching to Classic closes the drawer; only a layout change does (an effect on navOpen too would shut the
  // drawer the moment ☰ or a swipe opened it). Chats remembers whether its list was open on phones.
  useEffect(() => {
    if (layout === "classic") setNavOpen(false);
  }, [layout]);
  useEffect(() => {
    if (layout !== "classic" && isNarrow()) rememberChatsOpen(navOpen);
  }, [layout, navOpen]);
  /** Phones: the member list slides in from the right. */
  const [membersOpen, setMembersOpen] = useState(false);
  /** Desktop: the member list is hidden (phones ignore this; there it slides in on demand). */
  const [membersHidden, setMembersHidden] = useState(loadMembersHidden);
  const toggleMembersHidden = () =>
    setMembersHidden((h) => {
      saveMembersHidden(!h);
      return !h;
    });
  const [bannerHidden, setBannerHidden] = useState(() => {
    try {
      return sessionStorage.getItem("bloom:verify-banner") === "hidden";
    } catch {
      return false;
    }
  });
  const appRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const membersRef = useRef<HTMLElement>(null);
  const threadRef = useRef<HTMLElement>(null);
  // The swipe handler is attached once; it reads the latest state through these.
  const navOpenRef = useRef(false);
  const membersOpenRef = useRef(false);
  const threadOpenRef = useRef(false);
  const closeThreadRef = useRef<() => void>(() => {});
  navOpenRef.current = navOpen;
  membersOpenRef.current = membersOpen;
  /**
   * The "NEW" line for the open channel: the read position when it was opened, kept until you leave it. `synced`:
   * worked out with the room caught up (opened from the device cache, it's worked out again once the room has
   * told us what arrived meanwhile).
   */
  const [divider, setDivider] = useState<{ channelId: string; after: number | null; synced?: boolean; label?: string; tick?: number } | null>(null);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  /** A share from another app waiting for "Send to…" (share.ts). */
  const sharing = useSyncExternalStore(onShareChange, () => shareWaiting() && !!waitingShare());
  /** Where the open channel's message list is: at the bottom (so it's being read), and its newest message. */
  const bottom = useRef<{ channelId: string; at: boolean; newest: number } | null>(null);
  /**
   * A channel I marked unread while it's open (Discord's "Mark Unread"): it stays unread, even at the bottom, until I
   * leave it, press Esc or send something in it.
   */
  const heldUnread = useRef<string | null>(null);
  const visible = useVisible();
  const [resend, setResend] = useState<"idle" | "sending" | "sent" | string>("idle");
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [threadReplyTo, setThreadReplyTo] = useState<ChatMessage | null>(null);
  const [threadParentId, setThreadParentId] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [editing, setEditing] = useState<{ stream: string; id: string } | null>(null);
  /** The list panel: a list by id, or a channel's pins (which may not exist yet). */
  /** The side panel: a list, a channel's pins, or search (`search: true`). */
  const [openList, setOpenList] = useState<{ listId?: string; pinsOf?: string; search?: boolean } | null>(null);
  // Phones: Back (the back swipe, Android's back key) closes the thread or list panel and stays in its channel, rather
  // than leaving the channel (backCloses.ts). Desktop keeps Back for pages; its panel sits beside the chat.
  useBackCloses(isNarrow() && (!!threadParentId || !!openList), () => (openList ? setOpenList(null) : closeThread()));
  const [makingList, setMakingList] = useState(false);
  /** A list's Jump: go to the conversation, then bring the message into view (`n` so a repeat counts). */
  const [pendingJump, setPendingJump] = useState<{ root: string; stream: string; id: string } | null>(null);
  const [jumpReq, setJumpReq] = useState<{ stream: string; id: string; n: number } | null>(null);
  /** Renaming the open channel: the name being typed, and any error from the room. */
  const [renaming, setRenaming] = useState<{ name: string; error?: string } | null>(null);
  /** DMs: the new-DM picker, and the member card opened from the member list. */
  const [pickingDm, setPickingDm] = useState(false);
  /** Forwarding: the message whose picker is open, and the "Forwarded" toast afterwards. */
  const [forwarding, setForwarding] = useState<ChatMessage | null>(null);
  const [notifySheet, setNotifySheet] = useState(false);
  const [forwardToast, setForwardToast] = useState<string | null>(null);
  useEffect(() => {
    if (!linkToast) return;
    const t = setTimeout(() => setLinkToast(null), 1800);
    return () => clearTimeout(t);
  }, [linkToast]);
  useEffect(() => {
    if (!forwardToast) return;
    const t = setTimeout(() => setForwardToast(null), 2200);
    return () => clearTimeout(t);
  }, [forwardToast]);
  const [memberMenu, setMemberMenu] = useState<{ member: Member; rect: DOMRect } | null>(null);
  const [botMenu, setBotMenu] = useState<{ bot: BotInfo; rect: DOMRect } | null>(null);
  const threadComposer = useRef<ComposerHandle>(null);
  const composer = useRef<ComposerHandle>(null);
  const switchStarted = useRef<number | null>(null);

  const server = me?.servers.find((s) => s.id === serverId);

  useEffect(() => {
    if (!server || !me) return;
    const r = new Room(serverId, me.id);
    setRoom(r);
    setMetricsServer(serverId);
    // Names for the first paint, from the device, until the member list is fetched.
    void r.loaded.then(() => {
      if (r.cachedMembers) setMembers((m) => (m.length ? m : r.cachedMembers!));
    });
    return () => r.close();
  }, [serverId, !!server, me?.id]);

  const snap = useSyncExternalStore(room?.subscribe ?? noop, room?.getSnapshot ?? (() => EMPTY));
  /** What the room said when it refused something that names no message (a delete, an edit, a reaction), for a moment (#226). */
  const refusalToast = useRefusalToast(room, snap.notice);
  const bloomThread = inBloom ? (snap.blooming.find((t) => t.id === bloomParam) ?? null) : null;
  const channelId = inApps ? lastChannel.current : inBloom ? (bloomThread?.channelId ?? lastChannel.current) : channelParam;
  // A blooming thread's messages load while its page is open (like a thread panel).
  useEffect(() => {
    if (!room || !inBloom || !bloomParam) return;
    room.openThread(bloomParam);
    return () => room.closeThread(bloomParam);
  }, [room, inBloom, bloomParam]);

  // Switching channels drops a half-typed rename.
  useEffect(() => {
    setRenaming(null);
    setMembersOpen(false);
  }, [channelId]);

  const loadMembers = () =>
    api<Member[]>(`/servers/${serverId}/members`).then((list) => {
      rememberAvatars(list);
      setMembers(list);
    }, () => {});
  // Someone changed their name or avatar: names show from this list everywhere (messages, mentions, sidebars).
  useEffect(
    () =>
      room?.onMemberUpdated((p) => {
        setMembers((list) => applyProfile(list, p));
        if (p.id === me?.id) void refresh(); // me, from another device: the "me" panel reads the session
      }),
    [room, me?.id],
  );
  useEffect(() => {
    setMembers([]);
    if (server) void loadMembers();
  }, [serverId, !!server]);
  // Kept on the device for the next start.
  useEffect(() => {
    if (room && members.length) room.saveMembers(members);
  }, [room, members]);
  // Someone we don't know came online: they just joined. Refresh the member list.
  useEffect(() => {
    if ([...snap.online].some((id) => !members.some((m) => m.id === id))) void loadMembers();
  }, [snap.online]);

  // DMs are channels with kind "dm"; everything channel-shaped below (categories, "#" references) is text only.
  const textChannels = useMemo(() => snap.channels.filter((c) => c.kind !== "dm"), [snap.channels]);
  // No channel in the address (opening the server) or one that's gone: where I was last on this device, else
  // #general, else the first channel.
  const channel =
    snap.channels.find((c) => c.id === channelId) ??
    snap.channels.find((c) => c.id === lastChannelIn(serverId!)) ??
    textChannels.find((c) => c.name === "general") ??
    textChannels[0];
  const isDmOpen = channel?.kind === "dm";
  // Also how someone looking at a deleted channel moves on: it's gone from the list, so the first channel is chosen.
  useEffect(() => {
    if (inSettings || inApps || inBloom || !channel || channel.id === channelId) return;
    if (channelId ? !snap.synced : !snap.synced && !!lastChannelIn(serverId) && channel.id !== lastChannelIn(serverId)) return;
    navigate(`/s/${serverId}/${channel.id}`, { replace: true });
  }, [serverId, channel?.id, channelId, snap.synced, inSettings, inApps, inBloom]);
  if (!inApps && channel) lastChannel.current = channel.id;
  // Arriving at a DM I'd closed (a mention, search, a notification, a profile) opens it again, on all my devices (#131).
  // Only on arrival: closing the DM on screen moves away first, and must not undo itself here.
  useEffect(() => {
    if (channel?.kind === "dm" && snap.dmClosed[channel.id] !== undefined) room?.closeDm(channel.id, null);
  }, [channel?.id]);
  // Remember it once it's the channel actually on screen (the address caught up).
  useEffect(() => {
    if (!inSettings && !inApps && !inBloom && channel && channel.id === channelId) rememberVisit(serverId!, channel.id);
  }, [serverId, channel?.id, channelId, inSettings, inApps, inBloom]);

  // Renames, recolours, role changes and people leaving: re-read what depends on them.
  useEffect(
    () =>
      room?.onServerEvent((e) => {
        if (e.t === "server" || (e.t === "role" && e.userId === me?.id)) void refresh();
        if (e.t !== "server") void loadMembers();
      }),
    [room, me?.id],
  );

  const state = channel ? snap.byChannel[channel.id] : undefined;
  // The room catches up the channel on screen first, and prefetches it first.
  useEffect(() => room?.setFocus(channel?.id), [room, channel?.id]);

  // Channel switch time: click → the new channel's messages painted (PRD target: under 100 ms).
  useLayoutEffect(() => {
    if (switchStarted.current === null || !state) return;
    const t0 = switchStarted.current;
    switchStarted.current = null;
    requestAnimationFrame(() => record("channel_switch", performance.now() - t0));
  }, [channel?.id]);
  // Cold start: the first channel drawn with its messages, from the device cache or the room.
  useEffect(() => {
    if (state && (state.messages.length > 0 || snap.synced)) markColdStart();
  }, [snap.synced, !!state?.messages.length]);

  // Opening a channel (once we know where we'd read to) fixes where its "NEW" line goes until we leave it.
  if (channel && snap.me && (divider?.channelId !== channel.id || (!divider.synced && snap.synced))) {
    const r = snap.read[channel.id] ?? 0;
    if (divider?.channelId !== channel.id) heldUnread.current = null;
    setDivider({ channelId: channel.id, after: r < channel.lastSeq ? r : null, synced: snap.synced });
  }

  // A channel is read while it's open, the tab is visible and the list is at the bottom (not merely opened while
  // scrolled up). The list reports its position; coming back to the tab counts too.
  const onAtBottom = (id: string, at: boolean, newest: number) => {
    bottom.current = { channelId: id, at, newest };
    if (at && document.visibilityState === "visible" && heldUnread.current !== id) room?.markRead(id, newest);
  };
  useEffect(() => {
    const b = bottom.current;
    if (visible && b?.at && b.channelId === channel?.id && heldUnread.current !== b.channelId) room?.markRead(b.channelId, b.newest);
  }, [visible]);
  // The away seam (#403): back in a channel I never left, after 2 minutes away or more, the NEW line moves above what
  // arrived meanwhile and says since when. The list reports the last message in view when I leave.
  const seenProbe = useRef<(() => number) | null>(null);
  const seamTicks = useRef(0);
  const comeBack = useRef<{ mark: AwayMark; backAt: number; loading?: boolean } | null>(null);
  const openId = useRef<string | null>(null);
  openId.current = channel?.id ?? null;
  const settleAway = useRef(() => {});
  settleAway.current = () => {
    const back = comeBack.current;
    if (!back || !channel) return;
    // A channel marked unread by hand keeps the line where I put it.
    if (Date.now() - back.backAt > AWAY_SETTLE_MS || channel.id !== back.mark.channelId || heldUnread.current === channel.id) {
      comeBack.current = null;
      return;
    }
    const here = { channelId: channel.id, me: snap.me, messages: state?.messages ?? [], hasOlder: state?.more ?? false, lastSeq: channel.lastSeq };
    // A reconnect sends only the newest page. If more arrived while I was away, load back to the last message I saw
    // (as the open-time NEW line does) before the seam is placed and counted.
    const target = awayLoadBack(back.mark, back.backAt, here);
    if (target) {
      // Once per trip, and only when connected: the request is lost otherwise, and `synced` runs this again.
      if (!back.loading && snap.synced) {
        back.loading = true;
        void room?.loadBack(channel.id, target);
      }
      return;
    }
    const seam = awayDivider(back.mark, back.backAt, here);
    if (!seam) return;
    setDivider((d) => (d?.channelId === channel.id && d.after === seam.after ? { ...d, label: seam.label } : { channelId: channel.id, after: seam.after, synced: true, label: seam.label, tick: ++seamTicks.current }));
  };
  useEffect(
    () =>
      watchAway({
        mark: () => {
          const seen = seenProbe.current?.();
          return openId.current && seen !== undefined ? { channelId: openId.current, seenSeq: seen } : null;
        },
        onBack: (mark, backAt) => {
          comeBack.current = { mark, backAt };
          settleAway.current();
        },
      }),
    [],
  );
  useEffect(() => settleAway.current(), [state?.messages, snap.synced]);
  /** Mark unread from this message: it becomes the first unread, the NEW line moves above it, and the channel stays
   * unread while it's open. */
  const markUnread = (m: ChatMessage) => {
    if (!channel || !room || m.state || m.seq <= 0) return;
    heldUnread.current = channel.id;
    room.markUnread(channel.id, m.seq);
    setDivider({ channelId: channel.id, after: m.seq - 1, synced: true });
  };

  // @Bloom is in every server: its replies, mentions and quotes show its name like anyone's.
  // Bots too (docs/bots.md): published ones by their name, a bot still being built by its thread's title.
  const names = useMemo(
    () =>
      new Map([
        ...members.map((m) => [m.id, m.name] as [string, string]),
        [BLOOM_ID, BLOOM_NAME] as [string, string],
        ...snap.blooming.flatMap((t) => (t.botId ? [[t.botId, t.title] as [string, string]] : [])),
        ...snap.bots.map((b) => [b.id, b.name] as [string, string]),
      ]),
    [members, snap.bots, snap.blooming],
  );
  useEffect(() => rememberBots(snap.bots), [snap.bots]);
  // Colored names (#550, #698): the color the server stores for each member (`Member.nameColor`); a pick reaches here live, through
  // memberUpdated. The IRC layout keeps every name plain: it has its own nick colors.
  const nameColors = useMemo(() => (layout === "irc" ? null : new Map(members.map((m) => [m.id, m.nameColor] as [string, string]))), [members, layout]);

  // ---- The sidebar (#141): the layout in use picks which one, in web/src/sidebars.tsx ----
  const { data: myChats } = useMyChats(serverId, true);
  // Muted conversations right now (redrawn when a timed mute ends).
  const mutedIds = useMutedIds(snap.channels, snap.mutes, snap.serverNotify);
  const sidebar = useSidebarModel(layout, {
    serverId,
    me: snap.me,
    names,
    channels: snap.channels,
    categories: snap.categories,
    byChannel: snap.byChannel,
    read: snap.read,
    mentionCounts: snap.mentionCounts,
    dmClosed: snap.dmClosed,
    muted: mutedIds,
    activeId: channel?.id,
    myChats,
  });
  // Apps I may use: every app, but a bot only if it talks to me (docs/bots.md). Menus, the app home and AI tools follow.
  const myActor = { userId: snap.me ?? "", role: server?.role ?? "member" };
  const usableApps = useMemo(() => snap.apps.filter((a) => appUsableBy(a, snap.bots, myActor)), [snap.apps, snap.bots, snap.me, server?.role]);

  // App cards in the chat: live data, buttons calling actions, Open going to the app's page. Stable between
  // renders (the message rows are memoised on it).
  const streamRef = useRef<string | null>(null);
  streamRef.current = channel?.id ?? null;
  const membersKey = members.map((m) => `${m.id}:${m.name}`).join();
  const channelsKey = snap.channels.map((c) => `${c.id}:${c.name}`).join();
  // Drafts' cards (posted while trying a preview, in its blooming thread) draw from the preview's manifest.
  const previewApps = useMemo(() => snap.blooming.flatMap((t) => (t.preview ? [t.preview.app] : [])), [snap.blooming]);
  const cardHost = useMemo<CardHost | undefined>(
    () =>
      room
        ? {
            serverId,
            source: room.appSource,
            apps: previewApps.length ? [...snap.apps, ...previewApps] : snap.apps,
            me: snap.me,
            names,
            members: members.map((m) => ({ id: m.id, name: m.name })),
            channels: snap.channels.filter((c) => c.kind === "text").map((c) => ({ id: c.id, name: c.name })),
            call: (app, action, input) => room.callAction(app, action, input, streamRef.current ? { channelId: streamRef.current } : {}),
            open: (slug, route) => {
              setNavOpen(false);
              // A draft's card opens its blooming thread (where its preview is).
              if (slug.startsWith("~")) return navigate(`/s/${serverId}/bloom/${slug.slice(1)}`);
              navigate(`/s/${serverId}/apps/${slug}${route?.record ? `?record=${encodeURIComponent(route.record)}` : ""}`);
            },
          }
        : undefined,
    [room, snap.apps, previewApps, snap.me, names, membersKey, channelsKey, serverId],
  );
  // ---- Blooming ----
  const goBloom = (id: string) => {
    setNavOpen(false);
    navigate(`/s/${serverId}/bloom/${id}`);
  };
  const bloomById = useMemo(() => new Map(snap.blooming.map((t) => [t.id, t])), [snap.blooming]);
  const bloomRows = useMemo<BloomRows>(
    () => ({
      key: snap.blooming.map((t) => `${t.id}:${t.status}:${t.progress ?? ""}:${t.drafts}:${t.liveVersion}:${t.title}:${t.icon}`).join("|"),
      render: (m) => {
        const t = bloomById.get(m.id);
        return t ? <BloomChip thread={t} onOpen={() => goBloom(t.id)} /> : null;
      },
    }),
    [bloomById, serverId],
  );
  const canBloom = !!server && (snap.bloomWho === "everyone" || server.role !== "member");
  /** Starts a blooming thread (the "/" menu, the app home) and goes there. Null when it worked, else why not. */
  const startBlooming = async (inChannel: string, prompt: string, opts?: { bot?: boolean }): Promise<string | null> => {
    if (!room) return "Not connected yet.";
    const r = await room.bloomStart(inChannel, prompt, opts);
    if (!r.ok) return r.message;
    setNewApp(null);
    goBloom(r.threadId);
    return null;
  };
  const runCommand = async (stream: string, cmd: ParsedCommand, opts: { form?: boolean }) => {
    if (!room || !cmd.command) return;
    setCommandNote(null);
    const r = await room.command(stream, cmd.app.slug, cmd.command.name, opts.form ? {} : cmd.args);
    if (!r.ok) setCommandNote(r.message ?? "That command didn't work.");
  };
  const openApp = (slug: string, route?: Record<string, string>) => {
    setNavOpen(false);
    navigate(`/s/${serverId}/apps/${slug}${route?.record ? `?record=${encodeURIComponent(route.record)}` : ""}`);
  };
  const privateFooter = (stream: string | null | undefined) => {
    const list = stream && cardHost ? snap.privateCards.filter((c) => c.stream === stream) : [];
    if (!list.length) return null;
    return (
      <div className="private-cards">
        {list.map((pc) => (
          <PrivateCardView
            key={pc.id}
            id={pc.id}
            card={pc.card}
            host={cardHost!}
            onSubmit={(values) => room!.callAction(pc.app, pc.card.form?.action ?? pc.card.confirm?.action ?? "", values, { channelId: pc.stream, cardId: pc.id })}
            onDismiss={() => room?.dismissCard(pc.id)}
          />
        ))}
      </div>
    );
  };

  // Unread mentions in other channels (the open one is being read), for the tab title and the phone ☰ button.
  // A DM I closed (hidden until someone writes again) doesn't count.
  const hiddenDm = (id: string) => {
    const c = snap.channels.find((x) => x.id === id);
    return !!c && c.kind === "dm" && dmHidden(c.lastSeq, snap.dmClosed[id], false);
  };
  const mentionTotal = Object.entries(snap.mentionCounts).reduce((n, [id, c]) => ((id === channel?.id && visible) || hiddenDm(id) ? n : n + c), 0);

  const goTo = (id: string) => {
    if (id === channel?.id) return;
    switchStarted.current = performance.now();
    setNavOpen(false);
    navigate(`/s/${serverId}/${id}`);
  };
  // While settings are open, chat shortcuts are off (Esc there means "close settings").
  useChatShortcuts({
    markRead: () => {
      if (!channel || !room || inSettings) return;
      heldUnread.current = null;
      room.markRead(channel.id, channel.lastSeq);
      room.flushReads();
      setDivider({ channelId: channel.id, after: null });
    },
    openSwitcher: () => !inSettings && setSwitcherOpen(true),
    step: (dir, unread) => {
      if (inSettings) return;
      // The order the sidebar shows (the layout decides). Alt+Shift passes over muted channels.
      const c = stepTarget(sidebar.order, channel?.id, dir, unread, (c) => isUnread(c, snap), (id) => mutedIds.has(id));
      if (c) goTo(c.id);
    },
    escTaken: () => inSettings || !!renaming || !!editing,
  });

  const mainEl = () => appRef.current?.querySelector<HTMLElement>(":scope > .main");
  // Phones: swipe right on the chat to reveal the channels (or to close a thread), left to see who's here.
  // Panels follow the finger; letting go far or fast enough completes the move.
  useSwipe(appRef, {
    // Things that scroll sideways themselves (code blocks, the upload tray, a gallery) keep their drags.
    ignore: "textarea, input, .emoji-picker, .gif-picker, .media-picker, .autocomplete, .overlay, .embed-gallery, .sheet-backdrop, .list-picker, .msg-more, .md-pre, .upload-tray, .chat-chips",
    span: (dir) => {
      const dw = drawerRef.current?.offsetWidth ?? 320;
      const mw = membersRef.current?.offsetWidth ?? 280;
      if (dir === "right") return membersOpenRef.current ? mw : threadOpenRef.current ? window.innerWidth : dw;
      return navOpenRef.current ? dw : mw;
    },
    canRight: () => membersOpenRef.current || threadOpenRef.current || !navOpenRef.current,
    canLeft: () => navOpenRef.current || (!threadOpenRef.current && !membersOpenRef.current),
    // The chat moves with the panel (it slides aside like Discord's), so both follow the finger together.
    onDrag: (dx, dir) => {
      appRef.current?.classList.add("dragging");
      const set = (el: HTMLElement | null | undefined, tx: number) => el && (el.style.transform = `translateX(${tx}px)`);
      const fade = (v: number) => backdropRef.current && (backdropRef.current.style.opacity = String(Math.max(0, Math.min(1, v))));
      const main = mainEl();
      const dw = drawerRef.current?.offsetWidth ?? 320;
      const mw = membersRef.current?.offsetWidth ?? 280;
      if (dir === "right") {
        if (membersOpenRef.current) { const x = Math.min(mw, dx); set(membersRef.current, x); set(main, x - mw); fade(1 - dx / mw); }
        else if (threadOpenRef.current) {
          // The chat underneath comes back from a little to the left, like going back on iOS.
          const w = window.innerWidth;
          set(threadRef.current, dx);
          set(main, -0.25 * w * (1 - Math.min(1, dx / w)));
        } else if (!navOpenRef.current) { set(drawerRef.current, Math.min(0, -dw + dx)); set(main, Math.min(dw, dx)); fade(dx / dw); }
      } else if (navOpenRef.current) { const x = Math.max(-dw, dx); set(drawerRef.current, x); set(main, dw + x); fade(1 + dx / dw); }
      else { const x = Math.max(-mw, dx); set(membersRef.current, mw + x); set(main, x); fade(-dx / mw); }
    },
    onEnd: (commit, dir) => {
      appRef.current?.classList.remove("dragging");
      const main = mainEl();
      if (commit && dir === "right" && !membersOpenRef.current && threadOpenRef.current && threadRef.current) {
        // Finish the slide from where the finger let go, then close.
        const ease = "transform .22s cubic-bezier(.32,.72,0,1)";
        const panel = threadRef.current;
        panel.style.transition = ease;
        panel.style.transform = "translateX(100%)";
        if (main) {
          main.style.transition = ease;
          main.style.transform = "translateX(0)";
        }
        setTimeout(() => {
          closeThreadRef.current();
          main?.removeAttribute("style");
        }, 220);
        return;
      }
      for (const el of [drawerRef.current, membersRef.current, threadRef.current, backdropRef.current, main]) el?.removeAttribute("style");
      if (!commit) return;
      if (dir === "right") {
        if (membersOpenRef.current) setMembersOpen(false);
        else if (threadOpenRef.current) closeThreadRef.current();
        else setNavOpen(true);
      } else if (navOpenRef.current) setNavOpen(false);
      else setMembersOpen(true);
    },
  });

  // Leaving a channel closes its thread panel and drops a half-made reply, and a jump made there: still asked for when
  // you come back, it would jump to that message again instead of opening at the bottom.
  useEffect(() => {
    setReplyTo(null);
    setJumpReq(null);
    if (threadParentId) closeThread();
  }, [channel?.id]);
  // A list's Jump, once we're in the right conversation: open the thread if it's in one, then bring it into view.
  useEffect(() => {
    if (!pendingJump || channel?.id !== pendingJump.root) return;
    const j = pendingJump;
    setPendingJump(null);
    const go = () => {
      if (isThreadStream(j.stream)) openThread(parentOf(j.stream));
      setJumpReq((prev) => ({ stream: j.stream, id: j.id, n: (prev?.n ?? 0) + 1 }));
    };
    // A thread opens only once its first message is held here: load back to it first (it may be far up).
    const parent = isThreadStream(j.stream) ? parentOf(j.stream) : null;
    if (parent && room && !snap.byChannel[j.root]?.messages.some((m) => m.id === parent)) void room.loadBack(j.root, { id: parent }).then(go);
    else go();
  }, [channel?.id, pendingJump]);
  // A list that's gone (deleted, or its channel was) closes its panel.
  useEffect(() => {
    if (openList?.listId && snap.status === "live" && !snap.lists.some((l) => l.id === openList.listId)) setOpenList(null);
  }, [snap.lists, openList?.listId]);
  // Message links: the list's host (stable per room and conversation, so rows don't re-render for it) opens them
  // through openLinkRef, set below once the page's helpers exist.
  const openLinkRef = useRef<(ref: MessageLinkRef) => void>(() => {});
  const linkHost = useMemo<MessageLinkHost | undefined>(
    () => (room && channel ? { serverId, rootId: channel.id, resolve: (id) => room.messageRef(id), open: (ref) => openLinkRef.current(ref) } : undefined),
    [room, serverId, channel?.id],
  );
  // Opened at /s/<server>/<channel>/<message> (a pasted link): go to the message, then drop it from the address.
  const linkOpened = useRef<string | null>(null);
  useEffect(() => {
    if (!messageParam || !channelParam || !room || !snap.synced || linkOpened.current === messageParam) return;
    linkOpened.current = messageParam;
    navigate(`/s/${serverId}/${channelParam}`, { replace: true });
    openLinkRef.current({ serverId, channelId: channelParam, messageId: messageParam });
  }, [messageParam, channelParam, room, snap.synced]);
  // The quick switcher's rows (this server's conversations, then other servers' chats). Hooks stay above the
  // early returns below, so every render calls the same ones.
  const byServer = useMemo(() => new Map(me?.servers.map((s) => [s.id, s]) ?? []), [me?.servers]);
  const allNames = useMemo(
    () => new Map([...Object.entries(myChats?.people ?? {}).map(([id, p]) => [id, p.name] as [string, string]), ...names]),
    [myChats, names],
  );
  const switcherItems: SwitcherItem[] = useMemo(() => {
    const current: SwitcherItem[] = sidebarOrder(
      snap.channels,
      snap.categories,
      shownDms(snap.channels.filter((c) => c.kind === "dm"), snap.byChannel, channel?.id, snap.dmClosed),
    ).map((c) => ({
      channel: c,
      label: channelLabel(c, snap.me, names),
      group: c.kind === "dm" ? "Direct message" : snap.categories.find((k) => k.id === c.categoryId)?.name,
      unread: c.id !== channel?.id && isUnread(c, snap) && !mutedIds.has(c.id),
      mentions: c.id !== channel?.id ? (snap.mentionCounts[c.id] ?? 0) : 0,
    }));
    const others: SwitcherItem[] = (myChats?.chats ?? [])
      .filter((c) => c.serverId !== serverId && !c.closed)
      .map((c) => {
        const s = byServer.get(c.serverId);
        const label = c.kind === "dm" ? chatTitle(c, snap.me, allNames) : c.name;
        return {
          channel: { id: c.channelId, serverId: c.serverId, name: c.name, kind: c.kind },
          label,
          group: s?.name ?? (c.kind === "dm" ? "Direct message" : undefined),
          unread: c.unread && !c.muted,
          mentions: c.mentions,
        };
      });
    return [...current, ...others];
  }, [snap, channel?.id, names, mutedIds, myChats, serverId, byServer, allNames]);
  // Who from:/mentions: and what in: can name in search (people by their names here, bots and Bloom too).
  const searchPeople = useMemo(() => [...names].map(([id, name]) => ({ id, name })), [names]);
  const searchChannels = useMemo(() => snap.channels.filter((c) => c.kind === "text").map((c) => ({ id: c.id, name: c.name })), [snap.channels]);
  const serverUnread = useServerUnread(serverId);
  // The tab title's pip ("• Bloom"): anything unread in a conversation I haven't muted, here (live, not counting the
  // one I'm reading) or in my other servers (GET /me/unread, which leaves muted ones out too).
  const unreadAnywhere =
    snap.channels.some((c) => !(c.id === channel?.id && visible) && isUnread(c, snap) && !mutedIds.has(c.id) && !hiddenDm(c.id)) ||
    Object.entries(serverUnread).some(([id, u]) => id !== serverId && u.unread);
  // The title's count: this server's mentions and DM messages (live) plus my other servers' (GET /me/unread).
  const mentionsAnywhere = mentionTotal + Object.entries(serverUnread).reduce((n, [id, u]) => (id === serverId ? n : n + u.mentions), 0);
  useTitleBadge(mentionsAnywhere, unreadAnywhere);
  if (me === undefined) return null;
  if (me === null) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  if (!server) return <Navigate to="/" replace />;

  // The server's emoji and stickers, for everything drawn below (messages, pickers, settings).
  const withEmoji = (children: React.ReactNode) => (
    <NameColorsContext.Provider value={nameColors}>
      <ServerEmojiProvider serverId={serverId!} emojis={snap.emojis} stickers={snap.stickers} known={snap.status === "live"}>
        {children}
      </ServerEmojiProvider>
    </NameColorsContext.Provider>
  );

  if (inSettings) {
    return withEmoji(
      <ServerSettings
        server={server}
        me={me}
        room={room}
        snap={snap}
        members={members}
        onMembersChanged={() => void loadMembers()}
        onServerChanged={refresh}
        section={settingsSection}
        focusChannel={settingsFocus}
        currentChannel={channel?.id}
      />
    );
  }

  const isAdmin = server.role === "owner" || server.role === "admin";
  const canManage = canManageChannels(server.role);
  const canInvite = isAdmin || server.membersCanInvite;
  const online = members.filter((m) => snap.online.has(m.id));
  const offline = members.filter((m) => !snap.online.has(m.id));
  // The IRC layout's nick list: who is here (a DM's people, or the server's, @Bloom and the bots).
  const ircNicks = layout === "irc" ? nickEntries(members, snap.online, snap.bots, channel?.kind === "dm" ? channel.members : undefined) : [];
  const statusText = snap.status === "live" ? "Live" : snap.status === "connecting" ? "Connecting…" : "Offline, reconnecting…";
  const statusColor = snap.status === "live" ? "var(--online)" : snap.status === "connecting" ? "var(--idle)" : "var(--danger)";

  const threadId = threadParentId ? threadStreamId(threadParentId) : null;
  const threadState = threadId ? snap.byChannel[threadId] : undefined;
  const threadParent = threadParentId && state ? (state.messages.find((m) => m.id === threadParentId) ?? null) : null;
  function openThread(id: string) {
    setOpenList(null);
    if (threadParentId && threadParentId !== id) room?.closeThread(threadParentId);
    setThreadParentId(id);
    setThreadReplyTo(null);
    room?.openThread(id);
  }
  function closeThread() {
    if (threadParentId) room?.closeThread(threadParentId);
    // Likewise a jump into this thread, so opening it again starts at the bottom.
    setJumpReq((r) => (r && r.stream === threadId ? null : r));
    setThreadParentId(null);
    setThreadReplyTo(null);
  }
  /** Phones: the thread (or list) panel slides back out to the right before it goes, instead of vanishing. */
  function slideAway(done: () => void) {
    const el = threadRef.current;
    if (!el || !isNarrow() || matchMedia("(prefers-reduced-motion: reduce)").matches) return done();
    el.classList.add("leaving");
    setTimeout(done, 230);
  }
  // The list panel is a side panel like a thread: swiping right closes it.
  threadOpenRef.current = !!threadParentId || !!openList;
  closeThreadRef.current = () => (openList ? setOpenList(null) : closeThread());
  // Colored names (#550, #698): each typing name wears the person's color.
  const typingFor = (streamId: string | undefined): React.ReactNode => {
    if (!streamId) return "";
    const who = Object.entries(snap.typing[streamId] ?? {})
      .filter(([uid, exp]) => uid !== snap.me && exp > Date.now())
      .map(([uid]) => {
        const look = nameLook(nameColors, uid);
        return <span key={uid} className={look.className} style={look.style}>{names.get(uid) ?? "Someone"}</span>;
      });
    return who.length === 0 ? "" : who.length === 1 ? <>{who[0]} is typing…</> : who.length === 2 ? <>{who[0]} and {who[1]} are typing…</> : "Several people are typing…";
  };

  const inCategory = (id: string | null) => textChannels.filter((c) => c.categoryId === id).sort((a, b) => a.position - b.position);
  // "#" autocomplete (sidebar order, category as the label) and #channel links in messages.
  const channelOptions: ChannelOption[] = [
    ...inCategory(null).map((c) => ({ id: c.id, name: c.name })),
    ...[...snap.categories].sort((a, b) => a.position - b.position).flatMap((cat) => inCategory(cat.id).map((c) => ({ id: c.id, name: c.name, group: cat.name }))),
  ];
  const channelRefs: ChannelRefs = {
    byName: new Map(textChannels.map((c) => [c.name, c.id])),
    byId: new Map(textChannels.map((c) => [c.id, c.name])),
    open: (id) => {
      setNavOpen(false);
      navigate(`/s/${serverId}/${id}`);
    },
  };
  // "@" autocomplete: people online first, then the rest, by name.
  const byName = (x: Member, y: Member) => x.name.localeCompare(y.name);
  // In a DM, "@" only suggests the people in it.
  const inConversation = (m: Member) => channel?.kind !== "dm" || !!channel.members?.includes(m.id);
  const people: PersonOption[] = [
    ...[...online].filter(inConversation).sort(byName).map((m) => ({ id: m.id, name: m.name, online: true })),
    ...[...offline].filter(inConversation).sort(byName).map((m) => ({ id: m.id, name: m.name, online: false })),
    // Bots that would answer me here: on, they talk to me, and (outside a DM with them) in this channel.
    ...snap.bots
      .filter((b) => canTalkTo(b, myActor) && (channel?.kind === "dm" ? !!channel.members?.includes(b.id) : !!channel && b.channels.includes(channel.id)))
      .map((b) => ({ id: b.id, name: b.name, online: false, bot: true })),
  ];
  /** Stored text as typed, for reply excerpts and the thread header: "#name" and "@name" instead of tokens. */
  // Formatting marks dropped and spoilers kept hidden (these are one-line previews, not the message itself).
  const plainText = (t: string) => decodeMentions(decodeChannelRefs(markdownToPlain(t, { spoiler: SPOILER_MASK, time: formatStamp }), channelRefs.byId), names);
  const everyone = channel?.kind !== "dm";
  // How this conversation is named in the composer, thread panel and drop overlay: "#general", "@Maya", "Maya, Sam".
  const label = channel ? (isDmOpen ? dmLabel(channel, snap.me, names) : `#${channel.name}`) : "";
  const openDm = async (userIds: string[]): Promise<string | null> => {
    const r = await room?.openDm(userIds);
    if (!r?.ok) return r?.message ?? "Couldn't open that conversation.";
    setPickingDm(false);
    setMemberMenu(null);
    setMembersOpen(false);
    setNavOpen(false);
    switchStarted.current = performance.now();
    navigate(`/s/${serverId}/${r.channel.id}`);
    return null;
  };
  // ---- Lists and pins ----
  const listsById = new Map(snap.lists.map((l) => [l.id, l]));
  const pinsHere = channel ? room?.pinsOf(channel.id) : undefined;
  const openListPanel = (listId: string) => {
    if (threadParentId) closeThread();
    setMembersOpen(false);
    setNavOpen(false);
    setOpenList({ listId });
  };
  const listNote = (problem: string | null) => problem && setCommandNote(problem);
  // Me to the list rules (shared/src/lists.ts); the composer only offers lists I may add to.
  const listActor = { userId: snap.me ?? "", role: server.role === "owner" || server.role === "admin" ? server.role : "member" } as const;
  const addableLists = snap.lists.filter((l) => canAddTo(l, listActor));
  const listHost: ListHost | undefined =
    room && channel
      ? {
          serverId,
          all: snap.lists,
          byId: listsById,
          version: `${snap.lists.map((l) => `${l.id}:${l.name}:${l.emoji}:${l.addPolicy ?? ""}:${l.adders?.join("+") ?? ""}`).join()}|${Object.values(snap.privateItems).map((x) => x.join()).join(";")}`,
          listsOf: (m) => room.listsOf(m),
          inDm: !!isDmOpen,
          actor: listActor,
          pins: pinsHere,
          toggle: (m, listId, on) => void room.setListItem(m, listId, on).then(listNote),
          create: async (m, name, visibility) => {
            const r = await room.createList(name, visibility);
            if (!r.ok) return r.message;
            noteListUse(serverId, r.list.id);
            listNote(await room.setListItem(m, r.list.id, true));
            return null;
          },
          open: openListPanel,
        }
      : undefined;
  /** Where a message lives, for the list panel: "#general", "Maya", "thread in #general". */
  const whereIs = (stream: string, rootId: string) => {
    const c = snap.channels.find((x) => x.id === rootId);
    const base = !c ? "a conversation you can't see" : c.kind === "dm" ? channelLabel(c, snap.me, names) : `#${c.name}`;
    return isThreadStream(stream) ? `thread in ${base}` : base;
  };
  const jumpToItem = (it: ListItem) => {
    if (!snap.channels.some((c) => c.id === it.rootId)) return setCommandNote("That conversation isn't open to you.");
    if (window.matchMedia("(max-width: 760px)").matches) setOpenList(null);
    setPendingJump({ root: it.rootId, stream: it.channelId, id: it.messageId });
    if (it.rootId !== channel?.id) goTo(it.rootId);
  };
  /** A message link (clicked, or a preview tapped): to that message, if it's still there and mine to see. */
  const openMessageLink = (ref: MessageLinkRef) => {
    if (ref.serverId !== serverId) return navigate(messageLinkPath(ref));
    if (!room) return;
    void room.messageRef(ref.messageId).then((r) => {
      if (r === undefined) return setCommandNote("Couldn't reach the server. Try again?");
      if (!r) return setCommandNote("That message was deleted, or it's somewhere you can't see.");
      jumpToItem({ listId: "", messageId: r.message.id, channelId: r.message.channelId, rootId: r.rootId, addedBy: "", addedAt: 0 });
    });
  };
  openLinkRef.current = openMessageLink;
  /** A forwarded block's "from #channel": to the original, when it's in a conversation I can see. */
  const openForwarded = (f: Forward) => jumpToItem({ listId: "", messageId: f.messageId, channelId: f.stream, rootId: f.channelId, addedBy: "", addedAt: 0 });
  const forwardTargets = () =>
    sidebarOrder(snap.channels, snap.categories, shownDms(snap.channels.filter((c) => c.kind === "dm"), snap.byChannel, channel?.id, snap.dmClosed)).map((c) => ({
      channel: c,
      label: channelLabel(c, snap.me, names),
      group: c.kind === "dm" ? "Direct message" : snap.categories.find((k) => k.id === c.categoryId)?.name,
    }));
  /** One line describing a message for the forward picker. */
  const forwardPreview = (m: ChatMessage) => {
    const inner = m.forward && !m.body.trim() ? m.forward : null;
    const text = plainText(inner ? inner.body : m.body).replace(/\s+/g, " ").trim();
    const n = (inner ? inner.attachments : m.attachments)?.length ?? 0;
    return text || (n ? `${n} ${n === 1 ? "attachment" : "attachments"}` : "Message");
  };
  const panelList = openList ? (openList.listId ? listsById.get(openList.listId) : openList.pinsOf ? room?.pinsOf(openList.pinsOf) : undefined) : undefined;
  const sidebarLists = snap.lists.filter((l) => l.kind === "custom").sort((a, b) => Number(a.visibility === "private") - Number(b.visibility === "private") || a.name.localeCompare(b.name));

  const menuButton = (
          <button className="icon-btn mobile-only head-btn" onClick={() => setNavOpen(true)} aria-label={mentionTotal ? `Open ${sidebar.noun} (${mentionTotal} unread mentions)` : sidebar.anyUnread ? `Open ${sidebar.noun} (unread messages)` : `Open ${sidebar.noun}`}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
            {mentionTotal > 0 ? <span className="head-count" aria-hidden="true">{mentionTotal > 99 ? "99+" : mentionTotal}</span> : sidebar.anyUnread && <span className="head-dot" aria-hidden="true" />}
          </button>
  );
  // A bot I can't talk to still has an About page (who made it, who it talks to); nothing else of it is mine.
  const openedApp = appSlug ? snap.apps.find((a) => a.slug === appSlug) : undefined;
  const openedUsable = !!openedApp && usableApps.includes(openedApp);
  const openedBot = openedApp?.bloom?.botId ? (snap.bots.find((b) => b.id === openedApp.bloom!.botId) ?? null) : null;
  const appsMain = (
    <main className="main apps-main">
      <header className="main-head">
        {menuButton}
        <nav className="apps-crumbs" aria-label="Breadcrumb">
          {appSlug ? (
            <>
              <Link to={`/s/${serverId}/apps`} className="crumb-link">Apps</Link>
              <svg className="crumb-sep" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
              <h1 className="channel-title">{appSlug === "integrations" ? "Integrations" : (openedApp?.name ?? appSlug)}</h1>
            </>
          ) : (
            <h1 className="channel-title">Apps</h1>
          )}
        </nav>
        {channel && (
          <Link to={`/s/${serverId}/${channel.id}`} className="btn btn-small apps-back" title="Back to the conversation">
            Back to {channel.kind === "dm" ? "chat" : `#${channel.name}`}
          </Link>
        )}
      </header>
      {appSlug === "integrations" ? (
        <div className="apps-integrations">
          <IntegrationSettings server={server} members={members} live={snap.integrations} />
        </div>
      ) : !appSlug ? (
        <AppsHome
          integrations={snap.integrations}
          onIntegrations={() => navigate(`/s/${serverId}/apps/integrations`)}
          apps={usableApps}
          blooming={snap.blooming.filter((t) => t.private || textChannels.some((c) => c.id === t.channelId))}
          names={names}
          canBloom={canBloom}
          onOpen={(slug) => openApp(slug)}
          onOpenThread={goBloom}
          onNew={(kind) => setNewApp(kind)}
        />
      ) : openedApp && room ? (
        <AppPage
          key={openedApp.slug}
          app={openedApp}
          tab={!openedUsable ? "about" : appTab === "history" ? "history" : appTab === "about" ? "about" : "app"}
          room={room}
          me={{ id: me.id, name: me.name, role: server.role }}
          members={members}
          names={names}
          route={Object.fromEntries([...search.entries()].slice(0, 4))}
          channelId={() => lastChannel.current ?? channel?.id ?? null}
          onTab={(t) => navigate(`/s/${serverId}/apps/${openedApp.slug}${t === "app" ? "" : `/${t}`}`, { replace: true })}
          openApp={openApp}
          thread={openedApp.bloom ? (bloomById.get(openedApp.bloom.thread) ?? null) : null}
          onOpenThread={goBloom}
          channels={textChannels.map((c) => ({ id: c.id, name: c.name }))}
          defaultChannel={lastChannel.current && textChannels.some((c) => c.id === lastChannel.current) ? lastChannel.current : (textChannels[0]?.id ?? null)}
          bot={openedBot}
          onMessageBot={() => openedBot && void openDm([openedBot.id])}
          onDeleted={() => navigate(`/s/${serverId}/apps`, { replace: true })}
        />
      ) : (
        <div className="app-empty"><p className="muted">{snap.status === "live" ? "There's no such app here." : "Connecting…"}</p></div>
      )}
    </main>
  );

  // A blooming thread: its conversation (the thread's messages and a composer whose messages are prompts) beside the
  // live preview of its newest draft.
  const bloomStream = bloomThread ? threadStreamId(bloomThread.id) : null;
  const bloomState = bloomStream ? snap.byChannel[bloomStream] : undefined;
  const bloomParent = bloomThread && channel ? (snap.byChannel[bloomThread.channelId]?.messages.find((m) => m.id === bloomThread.id) ?? null) : null;
  const mayPrompt = !!bloomThread && (!bloomThread.locked || canEditThread(bloomThread, { id: me.id, role: server.role }, snap.apps));
  const bloomChat = bloomThread && bloomStream && (
    <>
      <MessageList
        channelId={bloomStream}
        messages={bloomState?.messages ?? []}
        names={names}
        me={snap.me}
        more={bloomState?.more ?? false}
        loadingOlder={bloomState?.loadingOlder ?? true}
        onLoadOlder={() => room?.loadOlder(bloomStream)}
        onLoadBack={(t) => room?.loadBack(bloomStream, t) ?? Promise.resolve()}
        onRetry={(id) => room?.retry(bloomStream, id)}
        onDiscard={(id) => room?.discard(bloomStream, id)}
        onReact={(id, emoji, on, superReact, jumbo) => room?.react(bloomStream, id, emoji, on, superReact, jumbo)}
        onReply={(m) => setThreadReplyTo(m)}
        onDelete={(m) => room?.remove(bloomStream, m.id)}
        canDelete={(m) => m.authorId === snap.me || isAdmin}
        onSuppressEmbeds={(m) => room?.suppressEmbeds(bloomStream, m.id)}
        onRemoveAttachment={(m, ref) => room?.removeAttachment(bloomStream, m.id, ref)}
        onSetAttachmentPrivate={(m, ref, on) => room?.setAttachmentPrivate(bloomStream, m.id, ref, on)}
        editingId={editing?.stream === bloomStream ? editing.id : null}
        onStartEdit={(m) => setEditing({ stream: bloomStream, id: m.id })}
        onEndEdit={(m, body) => {
          // Offline, nothing is sent: the notice says so and the edit stays open with its text (#514).
          if (body !== null && body.trim() !== m.body && room?.edit(bloomStream, m.id, body) === false) return;
          setEditing(null);
          threadComposer.current?.focus();
        }}
        empty="Tell Bloom what to build."
        channels={channelRefs}
        everyone
        cards={cardHost}
        footer={privateFooter(bloomStream)}
        header={
          <div className="thread-parent bloom-intro">
            {bloomParent && !bloomParent.deleted && (
              <>
                <div className="who">
                  <Avatar id={bloomParent.authorId} name={names.get(bloomParent.authorId) ?? "?"} size={24} />
                  <strong>{names.get(bloomParent.authorId) ?? "Someone"}</strong>
                </div>
                {bloomParent.body && <div className="body" style={{ marginTop: 4 }}>{plainText(bloomParent.body)}</div>}
              </>
            )}
            <div className="bloom-intro-note muted">
              {bloomThread.botId
                ? "🤖 Everything said here goes to Bloom's bot builder. Only you can see this thread; once it's published, add your bot to channels from its About tab."
                : `🌱 Everything said here goes to Bloom's app builder. ${bloomThread.locked ? "Only the app's owner and editors can prompt." : "Anyone in the server can join in."}`}
            </div>
          </div>
        }
      />
      <div className="composer-wrap">
        <TypingLine text={typingFor(bloomStream)} />
        {mayPrompt ? (
          <Composer
            ref={threadComposer}
            apps={usableApps}
            role={server.role}
            onCommand={(cmd, opts) => void runCommand(bloomStream, cmd, opts)}
            onOpenApp={(slug) => openApp(slug)}
            serverId={serverId}
            channelId={bloomStream}
            placeholder={bloomThread.drafts ? "Chat, or tag @Bloom to ask for changes…" : bloomThread.botId ? "Chat, or tag @Bloom to describe your bot…" : "Chat, or tag @Bloom to describe your app…"}
            customerId={me.id}
            channels={channelOptions}
            people={people}
            everyone
            replyingTo={threadReplyTo ? { name: names.get(threadReplyTo.authorId) ?? "Someone", excerpt: plainText(threadReplyTo.body) } : null}
            onCancelReply={() => setThreadReplyTo(null)}
            onSend={(t, attachments) => {
              room?.send(bloomStream, t, { replyTo: threadReplyTo ?? undefined, attachments });
              setThreadReplyTo(null);
            }}
            onTyping={() => room?.typing(bloomStream)}
            onEditLast={() => {
              const last = room?.lastOwnMessage(bloomStream);
              if (last) setEditing({ stream: bloomStream, id: last.id });
              return !!last;
            }}
          />
        ) : (
          <div className="bloom-locked-note" role="note">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4.5" y="10.5" width="15" height="10" rx="2.5" /><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" /></svg>
            Locked by the app's owner: only the owner and editors can prompt changes. You can still try the preview.
          </div>
        )}
      </div>
    </>
  );
  const bloomMain = bloomThread ? (
    <BloomingView
      thread={bloomThread}
      room={room!}
      me={{ id: me.id, name: me.name, role: server.role }}
      members={members}
      names={names}
      apps={snap.apps}
      chat={bloomChat}
      channelName={bloomThread.private ? "your DM with it" : textChannels.find((c) => c.id === bloomThread.channelId) ? `#${textChannels.find((c) => c.id === bloomThread.channelId)!.name}` : "the channel"}
      onBack={() => navigate(`/s/${serverId}/${bloomThread.channelId}`)}
      menuButton={menuButton}
      openApp={(slug) => openApp(slug)}
      cards={cardHost}
      onPrompt={(text) => threadComposer.current?.insertText(text)}
    />
  ) : (
    <main className="main">
      <header className="main-head">{menuButton}<h1 className="channel-title">Blooming thread</h1></header>
      <div className="app-empty"><p className="muted">{snap.status === "live" ? "This blooming thread doesn't exist (any more)." : "Connecting…"}</p></div>
    </main>
  );

  // What every layout's sidebar gets (and the IRC layout's switchbar, which draws outside it).
  const sidebarProps: Omit<SidebarProps, "names"> = {
    serverId,
    server,
    servers: me.servers,
    me: snap.me,
    online: snap.online,
    channels: snap.channels,
    categories: snap.categories,
    byChannel: snap.byChannel,
    read: snap.read,
    mentionCounts: snap.mentionCounts,
    dmClosed: snap.dmClosed,
    muted: mutedIds,
    activeId: channel?.id,
    inApps,
    inBloom,
    appSlug,
    collections: sidebarLists,
    openCollection: openList?.listId,
    listCount: snap.lists.length,
    isAdmin: canManage,
    canInvite,
    navOpen,
    inSettings,
    menu: {
      server,
      servers: me.servers,
      unread: serverUnread,
      canInvite,
      muted: muteActive(snap.serverNotify.mutedUntil),
      onSwitch: (id) => {
        setNavOpen(false);
        if (id !== serverId) navigate(`/s/${id}`);
      },
      onCreate: () => navigate("/new"),
      onJoin: () => navigate("/join"),
      onSettings: () => {
        setNavOpen(false);
        navigate(`/s/${serverId}/settings`);
      },
      onNotifications: () => {
        setNavOpen(false);
        setNotifySettings(true);
      },
      onInvite: () => {
        setNavOpen(false);
        setInviting(true);
      },
      onLeave: () => {
        setNavOpen(false);
        navigate(`/s/${serverId}/settings/leave`);
      },
    },
    footer: (
      <div className="me-panel">
      <Link to="/settings" state={{ from: location.pathname }} className="me-link" aria-label={`${me.name}: your settings`} onClick={() => setNavOpen(false)}>
        <Avatar id={me.id} name={me.name} size={28} presence={snap.status === "live" ? "online" : undefined} />
        <span className="me-name">{me.name}</span>
      </Link>
      <Link to="/settings" state={{ from: location.pathname }} className="icon-btn me-gear" aria-label="Settings" title="Settings" onClick={() => setNavOpen(false)}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" /></svg>
      </Link>
    </div>
    ),
    onNavigate: () => {
      switchStarted.current = performance.now();
      setNavOpen(false);
    },
    onCloseDrawer: () => setNavOpen(false),
    onInvite: () => {
      setNavOpen(false);
      setInviting(true);
    },
    onApps: () => {
      setNavOpen(false);
      navigate(`/s/${serverId}/apps`);
    },
    onCollection: openListPanel,
    onNewCollection: () => {
      setNavOpen(false);
      setMakingList(true);
    },
    onNewDm: () => setPickingDm(true),
    onConversationMenu: (id, origin) => setNotifyFor({ id, origin }),
    onCloseDm: (id, lastSeq) => {
      // Closing the DM on screen moves to a channel first, so the switch doesn't open it again (below).
      if (id === channel?.id) goTo(textChannels[0]?.id ?? "");
      room?.closeDm(id, lastSeq);
    },
    createChannel: async (name, categoryId) => {
      const r = await room?.createChannel(name, categoryId);
      if (!r) return "Not connected.";
      if (!r.ok) return r.message;
      navigate(`/s/${serverId}/${r.channel.id}`);
      return null;
    },
    createCategory: async (name) => {
      const r = await room?.createCategory(name);
      if (!r) return "Not connected.";
      return r.ok ? null : r.message;
    },
  };


  return withEmoji(
    <div ref={appRef} className={`app${layout === "irc" ? " irc theme-dark" : ""}${navOpen ? " nav-open" : ""}${membersOpen ? " members-open" : ""}${membersHidden ? " members-hidden" : ""}${threadParent && !inApps && !inBloom ? " thread-open" : ""}${inApps ? " apps-open" : ""}${inBloom ? " bloom-open" : ""}`}>
      <div className="drawer" ref={drawerRef}>
      {sidebar.render(sidebarProps)}
      </div>
      {sidebar.strip?.(sidebarProps, "desktop")}
      <div
        ref={backdropRef}
        className="nav-backdrop"
        aria-hidden="true"
        onClick={() => {
          setNavOpen(false);
          setMembersOpen(false);
        }}
      />

      {inApps ? appsMain : inBloom ? bloomMain : (
      <main
        className="main"
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes("Files")) {
            e.preventDefault();
            setDragging(true);
          }
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragging(false);
        }}
        onDrop={(e) => {
          if (!e.dataTransfer.files.length) return;
          e.preventDefault();
          setDragging(false);
          composer.current?.addFiles(e.dataTransfer.files);
        }}
      >
        <header className="main-head">
          {menuButton}
          {renaming && channel ? (
            <form
              className="rename-form"
              onSubmit={async (e) => {
                e.preventDefault();
                const name = renaming.name.trim();
                if (!name || name === channel.name) return setRenaming(null);
                const r = await room?.renameChannel(channel.id, name);
                if (r?.ok) setRenaming(null);
                else setRenaming({ name, error: r?.message ?? "Couldn't rename." });
              }}
            >
              <span className="muted" style={{ fontWeight: 500, fontSize: 16 }}>#</span>
              <input
                className="rename-input"
                aria-label="Channel name"
                autoFocus
                value={renaming.name}
                maxLength={40}
                onChange={(e) => setRenaming({ name: e.target.value.toLowerCase().replace(/\s+/g, "-") })}
                onKeyDown={(e) => e.key === "Escape" && setRenaming(null)}
                onBlur={(e) => {
                  if (!e.currentTarget.form?.contains(e.relatedTarget as Node | null)) e.currentTarget.form?.requestSubmit();
                }}
              />
              {renaming.error && <span className="error" style={{ fontSize: 12 }}>{renaming.error}</span>}
            </form>
          ) : isDmOpen && channel ? (
            <DmHeader channel={channel} me={snap.me} names={names} online={snap.online} />
          ) : (
            <div className="channel-heading">
              <h1 className="channel-title">
                <span className="muted channel-hash" style={{ fontWeight: 500 }}>#</span>
                <span className="channel-name">{channel?.name ?? ""}</span>
                {layout === "irc" && channel && <IrcModes count={ircNicks.length} modes={CHANNEL_MODES} />}
                {isAdmin && channel && (
                  <>
                    <button type="button" className="icon-btn rename-btn quick-rename" onClick={() => setRenaming({ name: channel.name })} aria-label="Rename channel" title="Rename channel">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                    </button>
                    <button type="button" className="icon-btn rename-btn" onClick={() => navigate(`/s/${serverId}/settings/channels/${channel.id}`)} aria-label="Edit channel" title="Edit channel">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" /></svg>
                    </button>
                  </>
                )}
              </h1>
              {channel?.topic && <div className="channel-topic" title={channel.topic}>{channel.topic}</div>}
            </div>
          )}
          <span className={`status-pill${snap.status === "live" ? " live" : ""}`} style={{ marginLeft: "auto" }} role="status" title={snap.status === "live" ? liveSince() : statusText}>
            <span className="status-dot" style={{ background: statusColor }} />
            <span className="status-text">{statusText}</span>
          </span>
          {channel && (
            <HeaderMore
              input={{
                members: true,
                online: online.length,
                pins: pinsHere?.count ?? 0,
                level: effectiveNotifyLevel(channel.kind, snap.notify[channel.id], snap.serverNotify),
                muted: isConversationMuted(channel.kind, snap.mutes[channel.id], snap.serverNotify),
              }}
              on={{
                members: () => setMembersOpen(true),
                pins: () => (threadParentId && closeThread(), setMembersOpen(false), setOpenList({ pinsOf: channel.id })),
                notifications: () => setNotifySheet(true),
              }}
            />
          )}
          <button
            type="button"
            className={`icon-btn head-btn search-btn${openList?.search ? " on" : ""}`}
            onClick={() => (openList?.search ? setOpenList(null) : (threadParentId && closeThread(), setMembersOpen(false), setOpenList({ search: true })))}
            aria-label="Search messages"
            title="Search"
          >
            <SearchIcon size={18} />
          </button>
          {channel && (
            <button
              type="button"
              className={`icon-btn desktop-only head-btn pins-btn${openList?.pinsOf === channel.id || (pinsHere && openList?.listId === pinsHere.id) ? " on" : ""}`}
              onClick={() => (openList?.pinsOf === channel.id ? setOpenList(null) : (threadParentId && closeThread(), setMembersOpen(false), setOpenList({ pinsOf: channel.id })))}
              aria-label={`Pinned messages${pinsHere?.count ? `, ${pinsHere.count}` : ""}`}
              title="Pinned messages"
            >
              <PinIcon size={18} />
              {!!pinsHere?.count && <span className="pins-count" aria-hidden="true">{pinsHere.count > 99 ? "99+" : pinsHere.count}</span>}
            </button>
          )}
          {channel && (
            <NotifyBell
              serverId={serverId}
              channel={channel}
              level={snap.notify[channel.id]}
              server={snap.serverNotify}
              mutedUntil={snap.mutes[channel.id]}
              onChange={(l) => room?.setNotify(channel.id, l)}
              onMute={(f) => room?.setMute(channel.id, f)}
              onServerSettings={() => setNotifySettings(true)}
            />
          )}
          {(() => {
            // Desktop: shows or hides the member list (remembered on this device). With a thread, search or a list
            // open in its place, it closes that and shows the members.
            const panelOpen = !!openList || !!threadParent;
            const shown = !panelOpen && !membersHidden;
            return (
              <button
                type="button"
                className={`icon-btn desktop-only head-btn members-btn${shown ? " on" : ""}`}
                onClick={() => {
                  if (panelOpen) {
                    if (openList) setOpenList(null);
                    else closeThread();
                    if (membersHidden) toggleMembersHidden();
                  } else toggleMembersHidden();
                }}
                aria-pressed={shown}
                aria-label={shown ? "Hide member list" : "Show member list"}
                title={shown ? "Hide member list" : "Show member list"}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M16 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 18.5V20" /><circle cx="10" cy="8" r="3.5" /><path d="M20 20v-1.5a3.5 3.5 0 0 0-2.5-3.35M15.5 4.7a3.5 3.5 0 0 1 0 6.6" /></svg>
              </button>
            );
          })()}
        </header>
        {sidebar.strip?.(sidebarProps, "phone")}
        {isDmOpen && channel && snap.me && <DmAiNotice serverId={serverId} channelId={channel.id} me={snap.me} names={names} />}
        {!me.emailVerified && !bannerHidden && (
          <div className="banner">
            <span className="banner-text">Confirm your email to invite people. We sent a link to {me.email}.</span>
            {resend === "idle" && (
              <button
                className="link-btn"
                onClick={() => {
                  setResend("sending");
                  auth.resendVerification(me.email).then(
                    () => setResend("sent"),
                    (e: Error) => setResend(e.message),
                  );
                }}
              >
                Resend
              </button>
            )}
            {resend === "sending" && <span className="muted">Sending…</span>}
            {resend === "sent" && <span className="muted">Sent. It can take a minute; check spam too.</span>}
            {!["idle", "sending", "sent"].includes(resend) && <span className="error">{resend}</span>}
            <CloseButton
              size="sm"
              className="banner-close"
              aria-label="Hide this notice"
              onClick={() => {
                setBannerHidden(true);
                try {
                  sessionStorage.setItem("bloom:verify-banner", "hidden");
                } catch {
                  // fine: it just comes back on reload
                }
              }}
            />
          </div>
        )}
        {channel && state ? (
          <MessageList
            channelId={channel.id}
            messages={state.messages}
            names={names}
            me={snap.me}
            more={state.more}
            loadingOlder={state.loadingOlder}
            onLoadOlder={() => room?.loadOlder(channel.id)}
            onLoadBack={(t) => room?.loadBack(channel.id, t) ?? Promise.resolve()}
            onRetry={(id) => room?.retry(channel.id, id)}
            onDiscard={(id) => room?.discard(channel.id, id)}
            onReact={(id, emoji, on, superReact, jumbo) => room?.react(channel.id, id, emoji, on, superReact, jumbo)}
            onReply={(m) => setReplyTo(m)}
            onDelete={(m) => room?.remove(channel.id, m.id)}
            canDelete={(m) => m.authorId === snap.me || isAdmin}
            onSuppressEmbeds={(m) => room?.suppressEmbeds(channel.id, m.id)}
            onRemoveAttachment={(m, ref) => room?.removeAttachment(channel.id, m.id, ref)}
            onSetAttachmentPrivate={(m, ref, on) => room?.setAttachmentPrivate(channel.id, m.id, ref, on)}
            editingId={editing?.stream === channel.id ? editing.id : null}
            onStartEdit={(m) => setEditing({ stream: channel.id, id: m.id })}
            onEndEdit={(m, body) => {
              // Offline, nothing is sent: the notice says so and the edit stays open with its text (#514).
              if (body !== null && body.trim() !== m.body && room?.edit(channel.id, m.id, body) === false) return;
              setEditing(null);
              composer.current?.focus();
            }}
            onOpenThread={(m) => (bloomById.has(m.id) ? goBloom(m.id) : openThread(m.id))}
            onForward={setForwarding}
            onOpenForward={openForwarded}
            onMarkUnread={markUnread}
            blooming={bloomRows}
            empty={isDmOpen ? `This is the beginning of your conversation with ${label.replace(/^@/, "")}.` : `This is the beginning of #${channel.name}. Say hi.`}
            start={layout === "irc" ? startLines(channel, label.replace(/^@/, "")).join("\n") : isDmOpen ? `This is the beginning of your conversation with ${label.replace(/^@/, "")}.` : undefined}
            channels={channelRefs}
            everyone={everyone}
            oneToOne={isDmOpen && (channel.members?.length ?? 0) <= 2}
            unreadAfter={divider?.channelId === channel.id ? divider.after : null}
            unreadLabel={divider?.channelId === channel.id ? divider.label : undefined}
            seamTick={divider?.channelId === channel.id ? (divider.tick ?? 0) : 0}
            seenProbe={seenProbe}
            lastSeq={channel.lastSeq}
            readSeq={snap.read[channel.id] ?? 0}
            onAtBottom={onAtBottom}
            cards={cardHost}
            footer={privateFooter(channel.id)}
            lists={listHost}
            links={linkHost}
            jumpRequest={jumpReq?.stream === channel.id ? jumpReq : null}
          />
        ) : (
          <div className="messages"><div className="load-older">Connecting…</div></div>
        )}
        <div className="composer-wrap">
          <TypingLine text={typingFor(channel?.id)} />
          {commandNote && (
            <div className="command-note" role="alert">
              {commandNote}
              <CloseButton size="sm" aria-label="Dismiss" onClick={() => setCommandNote(null)} />
            </div>
          )}
          {channel && (
            <Composer
              ref={composer}
              links={linkHost}
              apps={usableApps}
              role={server.role}
              onCommand={(cmd, opts) => void runCommand(channel.id, cmd, opts)}
              onOpenApp={(slug) => openApp(slug)}
              onBloom={canBloom && channel.kind === "text" ? (prompt, bot) => void startBlooming(channel.id, prompt, { bot }).then((err) => err && setCommandNote(err)) : undefined}
              serverId={serverId}
              channelId={channel.id}
              placeholder={`Message ${label}`}
              customerId={me.id}
              channels={channelOptions}
              people={people}
              everyone={everyone}
              replyingTo={replyTo ? { name: names.get(replyTo.authorId) ?? "Someone", excerpt: plainText(replyTo.body) } : null}
              onCancelReply={() => setReplyTo(null)}
              onSend={(t, attachments, lists) => {
                heldUnread.current = null;
                room?.send(channel.id, t, { replyTo: replyTo ?? undefined, attachments, lists });
                setReplyTo(null);
              }}
              lists={addableLists}
              inDm={!!isDmOpen}
              onCreateList={(name, visibility) => room?.createList(name, visibility) ?? Promise.resolve({ ok: false, message: "You're offline." })}
              onTyping={() => room?.typing(channel.id)}
              onEditLast={() => {
                const last = room?.lastOwnMessage(channel.id);
                if (last) setEditing({ stream: channel.id, id: last.id });
                return !!last;
              }}
            />
          )}
        </div>
        {dragging && <div className="drop-overlay" aria-hidden="true">Drop files to upload to {label}</div>}
      </main>
      )}

      {inApps || inBloom ? null : openList?.search && room ? (
        <SearchPanel
          source={room}
          names={names}
          people={searchPeople}
          channels={searchChannels}
          me={snap.me}
          channel={channel ? { id: channel.id, label: whereIs(channel.id, channel.id) } : null}
          where={whereIs}
          plain={plainText}
          onJump={(h) => jumpToItem({ listId: "", messageId: h.messageId, channelId: h.channelId, rootId: h.rootId, addedBy: "", addedAt: 0 })}
          onClose={() => setOpenList(null)}
          panelRef={threadRef}
        />
      ) : openList && room ? (
        <ListPanel
          key={openList.listId ?? `pins:${openList.pinsOf}`}
          list={panelList}
          emptyLabel={openList.pinsOf ? whereIs(openList.pinsOf, openList.pinsOf) : ""}
          source={room}
          names={names}
          me={snap.me}
          isAdmin={isAdmin}
          pinsInDm={panelList?.kind === "pins" && snap.channels.some((c) => c.id === panelList.channelId && c.kind === "dm")}
          where={whereIs}
          plain={plainText}
          onJump={jumpToItem}
          onClose={() => slideAway(() => setOpenList(null))}
          panelRef={threadRef}
        />
      ) : threadParent && channel ? (
        <aside className="thread-panel" aria-label="Thread" ref={threadRef}>
          <div className="thread-head">
            <button className="icon-btn mobile-only head-btn" onClick={() => slideAway(closeThread)} aria-label="Back to channel">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
            </button>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Thread</div>
              <div className="muted" style={{ fontSize: 12 }}>{label}</div>
            </div>
            <CloseButton className="desktop-only" onClick={closeThread} aria-label="Close thread" />
          </div>
          <MessageList
            channelId={threadId!}
            messages={threadState?.messages ?? []}
            names={names}
            me={snap.me}
            more={threadState?.more ?? false}
            loadingOlder={threadState?.loadingOlder ?? true}
            onLoadOlder={() => room?.loadOlder(threadId!)}
            onLoadBack={(t) => room?.loadBack(threadId!, t) ?? Promise.resolve()}
            onRetry={(id) => room?.retry(threadId!, id)}
            onDiscard={(id) => room?.discard(threadId!, id)}
            onReact={(id, emoji, on, superReact, jumbo) => room?.react(threadId!, id, emoji, on, superReact, jumbo)}
            onReply={(m) => setThreadReplyTo(m)}
            onDelete={(m) => room?.remove(threadId!, m.id)}
            canDelete={(m) => m.authorId === snap.me || isAdmin}
            onSuppressEmbeds={(m) => room?.suppressEmbeds(threadId!, m.id)}
            onRemoveAttachment={(m, ref) => room?.removeAttachment(threadId!, m.id, ref)}
            onSetAttachmentPrivate={(m, ref, on) => room?.setAttachmentPrivate(threadId!, m.id, ref, on)}
            editingId={editing?.stream === threadId ? editing.id : null}
            onStartEdit={(m) => setEditing({ stream: threadId!, id: m.id })}
            onEndEdit={(m, body) => {
              if (body !== null && body.trim() !== m.body) room?.edit(threadId!, m.id, body);
              setEditing(null);
              threadComposer.current?.focus();
            }}
            onForward={setForwarding}
            onOpenForward={openForwarded}
            empty="No replies yet. Start the thread."
            channels={channelRefs}
            everyone={everyone}
            cards={cardHost}
            footer={privateFooter(threadId)}
            lists={listHost}
            links={linkHost}
            jumpRequest={jumpReq?.stream === threadId ? jumpReq : null}
            header={
              <div className="thread-parent">
                <div className="who">
                  <Avatar id={threadParent.authorId} name={names.get(threadParent.authorId) ?? "?"} size={24} />
                  <strong>{names.get(threadParent.authorId) ?? "Someone"}</strong>
                </div>
                {threadParent.deleted && <div className="body muted" style={{ marginTop: 4 }}><em>Original message was deleted</em></div>}
                {threadParent.body && <div className="body" style={{ marginTop: 4 }}>{plainText(threadParent.body)}</div>}
                {!threadParent.body && threadParent.attachments?.length ? <div className="muted" style={{ fontSize: 13 }}>Attachment</div> : null}
                <div className="thread-count muted">{threadParent.thread?.count ?? 0} {threadParent.thread?.count === 1 ? "reply" : "replies"}</div>
              </div>
            }
          />
          <div className="composer-wrap">
            <TypingLine text={typingFor(threadId!)} />
            <Composer
              ref={threadComposer}
              links={linkHost}
              apps={usableApps}
              role={server.role}
              onCommand={(cmd, opts) => void runCommand(threadId!, cmd, opts)}
              onOpenApp={(slug) => openApp(slug)}
              serverId={serverId}
              channelId={threadId!}
              placeholder="Reply in thread"
              customerId={me.id}
              channels={channelOptions}
              people={people}
              everyone={everyone}
              replyingTo={threadReplyTo ? { name: names.get(threadReplyTo.authorId) ?? "Someone", excerpt: plainText(threadReplyTo.body) } : null}
              onCancelReply={() => setThreadReplyTo(null)}
              onSend={(t, attachments, lists) => {
                room?.send(threadId!, t, { replyTo: threadReplyTo ?? undefined, attachments, lists });
                setThreadReplyTo(null);
              }}
              lists={addableLists}
              inDm={!!isDmOpen}
              onCreateList={(name, visibility) => room?.createList(name, visibility) ?? Promise.resolve({ ok: false, message: "You're offline." })}
              onTyping={() => room?.typing(threadId!)}
              onEditLast={() => {
                const last = room?.lastOwnMessage(threadId!);
                if (last) setEditing({ stream: threadId!, id: last.id });
                return !!last;
              }}
            />
          </div>
        </aside>
      ) : (
        <aside className="members" aria-label="Members" ref={membersRef}>
          {layout === "irc" ? (
            <IrcNickList
              title={label}
              entries={ircNicks}
              me={snap.me}
              members={members}
              bots={snap.bots}
              onMember={(member, rect) => setMemberMenu({ member, rect })}
              onBot={(bot, rect) => setBotMenu({ bot, rect })}
              onClose={() => setMembersOpen(false)}
            />
          ) : (
            <>
          <div className="members-head mobile-only">
            <strong>Members</strong>
            <CloseButton onClick={() => setMembersOpen(false)} aria-label="Close members" />
          </div>
          <div className="section-label" style={{ padding: "12px 4px 4px" }}>ONLINE · {online.length}</div>
          {online.map((m) => (
            <button key={m.id} type="button" className="member" onClick={(e) => setMemberMenu({ member: m, rect: e.currentTarget.getBoundingClientRect() })}>
              <Avatar id={m.id} name={m.name} size={28} presence="online" />
              <span style={{ flex: 1 }}>{m.name}</span>
              {m.role !== "member" && <span className="role">{m.role.toUpperCase()}</span>}
            </button>
          ))}
          {offline.length > 0 && <div className="section-label" style={{ padding: "12px 4px 4px" }}>OFFLINE · {offline.length}</div>}
          {offline.map((m) => (
            <button key={m.id} type="button" className="member offline" onClick={(e) => setMemberMenu({ member: m, rect: e.currentTarget.getBoundingClientRect() })}>
              <Avatar id={m.id} name={m.name} size={28} />
              <span style={{ flex: 1 }}>{m.name}</span>
              {m.role !== "member" && <span className="role">{m.role.toUpperCase()}</span>}
            </button>
          ))}
          {snap.bots.length > 0 && <div className="section-label" style={{ padding: "12px 4px 4px" }}>BOTS · {snap.bots.length}</div>}
          {snap.bots.map((b) => (
            <button key={b.id} type="button" className={`member${b.disabled ? " offline" : ""}`} onClick={(e) => setBotMenu({ bot: b, rect: e.currentTarget.getBoundingClientRect() })}>
              <Avatar id={b.id} name={b.name} size={28} />
              <span style={{ flex: 1 }}>{b.name}</span>
              <span className="role">{b.disabled ? "OFF" : "BOT"}</span>
            </button>
          ))}
            </>
          )}
        </aside>
      )}

      {makingList && room && (
        <NewListDialog
          onClose={() => setMakingList(false)}
          onCreate={async (name, emoji, visibility) => {
            const r = await room.createList(name, visibility, emoji);
            if (!r.ok) return r.message;
            openListPanel(r.list.id);
            return null;
          }}
        />
      )}
      {pickingDm && <NewDmPicker members={members} me={me.id} online={snap.online} onClose={() => setPickingDm(false)} onOpen={openDm} />}
      {memberMenu && (
        <MemberMenu
          member={memberMenu.member}
          anchor={memberMenu.rect}
          online={snap.online.has(memberMenu.member.id)}
          isMe={memberMenu.member.id === me.id}
          onMessage={() => void openDm([memberMenu.member.id])}
          onClose={() => setMemberMenu(null)}
        />
      )}
      {botMenu && (
        <BotMenu
          bot={botMenu.bot}
          ownerName={names.get(botMenu.bot.ownerId) ?? "someone who left"}
          anchor={botMenu.rect}
          canMessage={canTalkTo(botMenu.bot, myActor)}
          onMessage={() => {
            setBotMenu(null);
            void openDm([botMenu.bot.id]);
          }}
          onOpen={() => {
            setBotMenu(null);
            navigate(`/s/${serverId}/apps/${botMenu.bot.app}/about`);
          }}
          onClose={() => setBotMenu(null)}
        />
      )}
      {forwarding && (
        <ForwardDialog
          targets={forwardTargets()}
          preview={forwardPreview(forwarding)}
          onClose={() => setForwarding(null)}
          onSend={(ids, note) => {
            for (const id of ids) room?.forward(id, forwarding, note);
            const first = snap.channels.find((c) => c.id === ids[0]);
            const where = first ? (first.kind === "dm" ? channelLabel(first, snap.me, names) : `#${first.name}`) : "";
            setForwarding(null);
            setForwardToast(ids.length > 1 ? `Forwarded to ${ids.length} conversations` : `Forwarded to ${where}`);
          }}
        />
      )}
      {notifySheet && channel && (
        // The phone header's ⋯ → Notifications (#348): the bell's menu, in a sheet.
        <Sheet label="Notification settings" onClose={() => setNotifySheet(false)}>
          <NotifyMenu
            channel={channel}
            level={snap.notify[channel.id]}
            server={snap.serverNotify}
            mutedUntil={snap.mutes[channel.id]}
            onChange={(l) => room?.setNotify(channel.id, l)}
            onMute={(f) => room?.setMute(channel.id, f)}
            onServerSettings={() => setNotifySettings(true)}
            onClose={() => setNotifySheet(false)}
          />
        </Sheet>
      )}
      {forwardToast && <div className="toast" role="status">{forwardToast}</div>}
      {refusalToast && <div className="toast toast-high" role="status">{refusalToast}</div>}
      {(switcherOpen || sharing) && (
        <QuickSwitcher
          items={switcherItems}
          currentId={channel?.id}
          // A share from another app (share.ts): "Send to…"; the picked conversation's composer takes it.
          sendTo={sharing ? { heading: "Send to…", what: describeShare(waitingShare()!) } : undefined}
          onPick={(id, targetServerId) => {
            if (sharing) sendShareTo(id);
            setSwitcherOpen(false);
            if (targetServerId && targetServerId !== serverId) {
              navigate(`/s/${targetServerId}/${id}`);
            } else {
              goTo(id);
              // A frame later, when the switcher is gone. Not if something else took the focus meanwhile (the switcher opened again, #376).
              requestAnimationFrame(() => focusIfIdle(composer.current));
            }
          }}
          onClose={() => {
            if (sharing) dropShare();
            setSwitcherOpen(false);
          }}
        />
      )}
      {newApp && (
        <NewAppDialog
          kind={newApp}
          channels={textChannels.map((c) => ({ id: c.id, name: c.name }))}
          defaultChannel={lastChannel.current && textChannels.some((c) => c.id === lastChannel.current) ? lastChannel.current : (textChannels[0]?.id ?? null)}
          onStart={startBlooming}
          onClose={() => setNewApp(null)}
        />
      )}
      {notifyFor &&
        (() => {
          const c = snap.channels.find((x) => x.id === notifyFor.id);
          if (!c) return null;
          const { origin } = notifyFor;
          const sheet = !!origin.touch || isNarrow();
          const close = (refocus = false) => {
            setNotifyFor(null);
            if (refocus) origin.from.focus({ preventScroll: true });
          };
          const label = c.kind === "dm" ? dmLabel(c, snap.me, names) : `#${c.name}`;
          const actions = rowActions({
            kind: c.kind === "dm" ? "dm" : "text",
            hasUnread: c.id !== channel?.id && (isUnread(c, snap) || (snap.mentionCounts[c.id] ?? 0) > 0),
            canEditChannels: isAdmin,
            canClose: c.kind === "dm",
          });
          const run = {
            read: () => {
              room?.markRead(c.id, c.lastSeq);
              room?.flushReads();
            },
            link: () => {
              const copy = navigator.clipboard?.writeText(channelUrl(window.location.origin, serverId, c.id));
              if (!copy) return setLinkToast("Couldn't copy");
              void copy.then(() => setLinkToast("Link copied"), () => setLinkToast("Couldn't copy"));
            },
            settings: () => {
              setNavOpen(false);
              navigate(`/s/${serverId}/settings/channels/${c.id}`);
            },
            close: () => {
              // Closing the DM on screen moves to a channel first, so the switch doesn't open it again (below).
              if (c.id === channel?.id) goTo(textChannels[0]?.id ?? "");
              room?.closeDm(c.id, c.lastSeq);
            },
          };
          const menu = (
            <NotifyMenu
              heading={label}
              channel={c}
              level={snap.notify[c.id]}
              server={snap.serverNotify}
              mutedUntil={snap.mutes[c.id]}
              onChange={(l) => room?.setNotify(c.id, l)}
              onMute={(f) => room?.setMute(c.id, f)}
              onServerSettings={() => setNotifySettings(true)}
              onClose={() => close()}
              before={<RowActionGroup actions={actions.before} label="Unread" run={run} onClose={() => close()} />}
              after={<RowActionGroup actions={actions.after} label="Conversation" run={run} onClose={() => close()} />}
            />
          );
          return sheet ? (
            <Sheet label="Notification settings" onClose={() => close()}>
              {menu}
            </Sheet>
          ) : (
            <DesktopRowMenu origin={origin} label={`Menu for ${label}`} onClose={close}>
              {menu}
            </DesktopRowMenu>
          );
        })()}
      {linkToast && <div className="toast" role="status">{linkToast}</div>}
      {notifySettings && (
        <ServerNotifySettings
          serverName={server.name}
          server={snap.serverNotify}
          channels={sidebarOrder(snap.channels, snap.categories, snap.channels.filter((c) => c.kind === "dm"))}
          levels={snap.notify}
          mutes={snap.mutes}
          labelFor={(c) => (c.kind === "dm" ? dmLabel(c as Channel, snap.me, names) : `#${c.name}`)}
          onServer={(patch) => room?.setServerNotify(patch)}
          onMute={(id, f) => room?.setMute(id, f)}
          onLevel={(id, l) => room?.setNotify(id, l)}
          onClose={() => setNotifySettings(false)}
        />
      )}
      {inviting && <InviteModal serverId={serverId} serverName={server.name} isAdmin={isAdmin} verified={me.emailVerified} email={me.email} paused={!server.acceptingMembers} onClose={() => setInviting(false)} />}
    </div>
  );
}
